import axios from 'axios';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:6001/api'; // Local development
const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api'; // Live URL

export interface Subscription {
  status: string;
  plan: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  subscription?: Subscription;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
}

// Mock data for development or when API fails
const MOCK_USER: User = {
  id: 'user_123456',
  email: 'demo@example.com',
  firstName: 'Demo',
  lastName: 'User',
  subscription: {
    status: 'active',
    plan: 'PREMIUM',
    currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    cancelAtPeriodEnd: false
  }
};

// Check if an exchange ticket is present in URL query parameters
export const hasPendingExchangeTicket = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const ticket = urlParams.get('ticket');
    return Boolean(ticket && ticket.trim().length > 5);
  } catch {
    return false;
  }
};

let pendingExchangePromise: Promise<string | null> | null = null;

// Atomically redeem exchange ticket with backend and store auth token
export const exchangeTicketAsync = async (explicitTicket?: string): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  if (pendingExchangePromise) {
    return pendingExchangePromise;
  }

  pendingExchangePromise = (async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const ticket = (explicitTicket || urlParams.get('ticket') || '').trim();

      if (!ticket) {
        return null;
      }

      // Immediately scrub ticket from address bar so it never lingers in browser history
      urlParams.delete('ticket');
      const cleanSearch = urlParams.toString();
      const newUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      console.log('Exchanging auth ticket for session...');
      const response = await axios.post(`${API_URL}/auth/exchange-ticket`, { ticket }, {
        timeout: 10000
      });

      if (response.data && response.data.success && response.data.token) {
        const { token } = response.data;
        setToken(token);
        console.log('Exchange ticket successfully redeemed');
        return token;
      }
    } catch (err: any) {
      console.error('Failed to exchange ticket:', err?.response?.data?.error || err.message);
    } finally {
      pendingExchangePromise = null;
    }
    return null;
  })();

  return pendingExchangePromise;
};

// Helper to inspect URL query params or cookies for auto-login tokens (e.g. from Framer checkout)
export const checkAndConsumeUrlToken = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Check URL query parameters: ?token=... or ?auth_token=...
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token') || urlParams.get('auth_token');

    if (urlToken && urlToken.length > 10) {
      setToken(urlToken);

      // Clean up URL without reloading page so token isn't persisted in browser history
      urlParams.delete('token');
      urlParams.delete('auth_token');
      const cleanSearch = urlParams.toString();
      const newUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      return urlToken;
    }

    // 2. Check cookie adsmash_token (shared across .adsmash.org)
    if (typeof document !== 'undefined' && document.cookie) {
      const match = document.cookie.match(/(?:^|;\s*)adsmash_token=([^;]+)/);
      if (match && match[1]) {
        const cookieToken = decodeURIComponent(match[1]);
        if (cookieToken && cookieToken.length > 10) {
          const currentToken = localStorage.getItem('token');
          if (!currentToken) {
            setToken(cookieToken);
          }
          return cookieToken;
        }
      }
    }
  } catch (err) {
    console.error('Error checking auto-login token:', err);
  }

  return null;
};

// Store the token in localStorage
export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
  console.log('Token stored in localStorage');
  // Dispatch event to notify UserContext of token change
  window.dispatchEvent(new Event('tokenChanged'));
};

// Get the token from localStorage
export const getToken = (): string | null => {
  let token = localStorage.getItem('token');
  if (!token) {
    token = checkAndConsumeUrlToken();
  }
  return token;
};

// Check if user is logged in
export const isLoggedIn = (): boolean => {
  const token = getToken();
  return !!token;
};

// Remove token from localStorage
export const logout = (): void => {
  localStorage.removeItem('token');
  console.log('Token removed from localStorage, user logged out');
  // Dispatch event to notify UserContext of token removal
  window.dispatchEvent(new Event('tokenChanged'));
};

// Login user
export const login = async (email: string, password: string, useMockOnFailure = true): Promise<AuthResponse> => {
  try {
    console.log('Logging in user:', email);
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, {
      timeout: 5000, // 5 second timeout
    });
    console.log('Login successful');
    
    if (response.data.success) {
      const { token, user } = response.data;
      setToken(token);
      return response.data;
    } else {
      throw new Error(response.data.error || 'Login failed');
    }
  } catch (error: any) {
    console.error('Login failed:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    if (useMockOnFailure && email === 'demo@example.com' && password === 'password') {
      console.log('Using mock login data for demo account');
      const mockToken = 'mock.jwt.token';
      setToken(mockToken);
      return { 
        success: true,
        token: mockToken, 
        user: MOCK_USER 
      };
    }
    throw error;
  }
};

// Register user
export const register = async (email: string, password: string, firstName: string, lastName: string, useMockOnFailure = true): Promise<AuthResponse> => {
  try {
    console.log('Registering user:', email);
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      firstName,
      lastName
    }, {
      timeout: 5000, // 5 second timeout
    });
    console.log('Registration successful');
    
    if (response.data.success) {
      const { token, user } = response.data;
      setToken(token);
      return response.data;
    } else {
      throw new Error(response.data.error || 'Registration failed');
    }
  } catch (error: any) {
    console.error('Registration failed:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    if (useMockOnFailure) {
      console.log('Using mock registration data');
      const mockToken = 'mock.jwt.token';
      setToken(mockToken);
      return { 
        success: true,
        token: mockToken, 
        user: {
          ...MOCK_USER,
          email,
          firstName,
          lastName,
          subscription: {
            status: 'none',
            plan: 'none',
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false
          }
        }
      };
    }
    throw error;
  }
};

// Get current user
export const getCurrentUser = async (useMockOnFailure = true): Promise<User | null> => {
  try {
    let token = getToken();
    if (!token && hasPendingExchangeTicket()) {
      token = await exchangeTicketAsync();
    }
    if (!token) {
      console.log('No token found, user not authenticated');
      return null;
    }
    
    console.log('Fetching current user data');
    const response = await axios.get(`${API_URL}/user/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      timeout: 5000, // 5 second timeout
    });
    console.log('User data retrieved successfully');
    
    if (response.data.success) {
      return response.data.user;
    } else {
      throw new Error(response.data.error || 'Failed to fetch user data');
    }
  } catch (error: any) {
    console.error('Failed to fetch user data:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    if (useMockOnFailure) {
      console.log('Using mock user data');
      return MOCK_USER;
    }
    
    logout();
    return null;
  }
};

// Update user profile
export const updateUserProfile = async (userData: {
  firstName?: string;
  lastName?: string;
  email?: string;
}, useMockOnFailure = true): Promise<User> => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('No token found, user not authenticated');
    }
    
    console.log('Updating user profile');
    const response = await axios.put(`${API_URL}/user/update`, userData, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      timeout: 5000, // 5 second timeout
    });
    console.log('User profile updated successfully');
    
    if (response.data.success) {
      return response.data.user;
    } else {
      throw new Error(response.data.error || 'Failed to update user profile');
    }
  } catch (error: any) {
    console.error('Failed to update user profile:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    if (useMockOnFailure) {
      console.log('Using mock updated user data');
      return {
        ...MOCK_USER,
        ...userData
      };
    }
    
    throw error;
  }
};

// Setup axios interceptors to add auth token to all requests
export const setupAxiosInterceptors = (): void => {
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        console.log('Received 401 Unauthorized, logging out user');
        logout();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
  
  console.log('Axios interceptors set up');
}; 
